import React, { useState, useEffect } from 'react';
import apiClient from '../../assets/scripts/services/apiClient';
import MultiOrgSelector from './MultiOrgSelector';
import PlatformEndpointsEditor from './PlatformEndpointsEditor';

const ApplicationModal = ({ isOpen, onClose, onSave, application = null, title, organizations = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    detail: {},
    // Support both old and new structure
    organization_associations: [],
    // Keep legacy fields for backward compatibility
    organization_id: null,
    user_id_access_list: [],
    // New optional sync fields
    application_sync_endpoint: '',
    application_sync_api_key: '',
    // Service worker flag
    is_service_worker: false,
    // Platform communication endpoints
    platform_communication_endpoints: null
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [newAccessKey, setNewAccessKey] = useState('');
  const [loadingAppData, setLoadingAppData] = useState(false);

  useEffect(() => {
    const loadApplicationData = async () => {
      if (application && isOpen) {
        // For editing, fetch fresh application data to ensure we have all organizations
        setLoadingAppData(true);
        try {
          const freshData = await apiClient.get(`/applications/${application.id}`);
          
          console.log('Loaded application data:', freshData);
          console.log('Platform endpoints from API:', freshData.platform_communication_endpoints);
          
          // Handle both new organizations array and legacy single organization
          let orgAssociations = [];
          
          if (freshData.organizations && freshData.organizations.length > 0) {
            // New structure with multiple organizations
            orgAssociations = freshData.organizations.map(org => ({
              organization_id: org.id,
              organization_name: org.name,
              user_id_access_list: org.users ? org.users.map(u => u.id) : [],
              association_id: org.association_id
            }));
          } else if (freshData.organization) {
            // Legacy single organization - convert to array format
            orgAssociations = [{
              organization_id: freshData.organization.id,
              organization_name: freshData.organization.name,
              user_id_access_list: freshData.users ? freshData.users.map(u => u.id) : []
            }];
          }
          
          setFormData({
            name: freshData.name || '',
            description: freshData.description || '',
            detail: freshData.detail || {},
            organization_associations: orgAssociations,
            // Keep legacy fields for backward compatibility
            organization_id: freshData.organization?.id || null,
            user_id_access_list: freshData.users ? freshData.users.map(u => u.id) : [],
            // New optional sync fields
            application_sync_endpoint: freshData.application_sync_endpoint || '',
            application_sync_api_key: freshData.application_sync_api_key || '',
            // Service worker flag
            is_service_worker: freshData.is_service_worker || false,
            // Platform communication endpoints
            platform_communication_endpoints: freshData.platform_communication_endpoints || null
          });
        } catch (err) {
          console.error('Error fetching application data:', err);
          // Fall back to passed-in data if fetch fails
          let orgAssociations = [];
          
          if (application.organizations && application.organizations.length > 0) {
            orgAssociations = application.organizations.map(org => ({
              organization_id: org.id,
              organization_name: org.name,
              user_id_access_list: org.users ? org.users.map(u => u.id) : [],
              association_id: org.association_id
            }));
          } else if (application.organization) {
            orgAssociations = [{
              organization_id: application.organization.id,
              organization_name: application.organization.name,
              user_id_access_list: application.users ? application.users.map(u => u.id) : []
            }];
          }
          
          setFormData({
            name: application.name || '',
            description: application.description || '',
            detail: application.detail || {},
            organization_associations: orgAssociations,
            organization_id: application.organization?.id || null,
            user_id_access_list: application.users ? application.users.map(u => u.id) : [],
            // New optional sync fields
            application_sync_endpoint: application.application_sync_endpoint || '',
            application_sync_api_key: application.application_sync_api_key || '',
            // Service worker flag
            is_service_worker: application.is_service_worker || false,
            // Platform communication endpoints
            platform_communication_endpoints: application.platform_communication_endpoints || null
          });
        } finally {
          setLoadingAppData(false);
        }
      } else if (!application && isOpen) {
        // New application
        setFormData({
          name: '',
          description: '',
          detail: {},
          organization_associations: [],
          organization_id: null,
          user_id_access_list: [],
          // New optional sync fields
          application_sync_endpoint: '',
          application_sync_api_key: '',
          // Service worker flag
          is_service_worker: false,
          // Platform communication endpoints
          platform_communication_endpoints: null
        });
      }
      setErrors({});
      setShowAccessKey(false);
      setNewAccessKey('');
    };

    if (isOpen) {
      loadApplicationData();
    }
  }, [application, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('detail.')) {
      const detailKey = name.replace('detail.', '');
      setFormData(prev => ({
        ...prev,
        detail: {
          ...prev.detail,
          [detailKey]: value
        }
      }));
    } else if (type === 'checkbox') {
      // Special handling for service worker toggle
      if (name === 'is_service_worker' && checked) {
        // Clear organization associations when enabling service worker
        setFormData(prev => ({
          ...prev,
          [name]: checked,
          organization_associations: [],
          organization_id: null,
          user_id_access_list: []
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleOrganizationAssociationsChange = (associations) => {
    setFormData(prev => ({
      ...prev,
      organization_associations: associations
    }));
  };

  const handlePlatformEndpointsChange = (endpoints) => {
    setFormData(prev => ({
      ...prev,
      platform_communication_endpoints: endpoints
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Application name is required';
    }

    // Only validate organizations if not a service worker
    if (!formData.is_service_worker && formData.organization_associations.length === 0) {
      newErrors.organizations = 'At least one organization must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Prepare submission data with new structure
      const submitData = {
        name: formData.name,
        description: formData.description,
        detail: {
          ...formData.detail,
          name: formData.name, // Required in detail object
          application_purpose: formData.detail.application_purpose || '',
          application_author: formData.detail.application_author || '',
          version: formData.detail.version || '1.0.0',
          environment: formData.detail.environment || 'production',
          status: formData.detail.status || 'active'
        },
        // Add optional sync fields (send null if empty)
        application_sync_endpoint: formData.application_sync_endpoint.trim() || null,
        application_sync_api_key: formData.application_sync_api_key.trim() || null,
        // Service worker flag
        is_service_worker: formData.is_service_worker,
        // Platform communication endpoints
        platform_communication_endpoints: formData.platform_communication_endpoints || null
      };

      // Use new structure for create/update
      if (application) {
        // Update existing application
        // For updates, only send organization associations if not a service worker
        const response = await onSave({
          ...submitData,
          organization_associations: formData.is_service_worker ? [] : formData.organization_associations
        });
        
        onClose();
      } else {
        // Create new application with organization_associations (only if not a service worker)
        if (!formData.is_service_worker) {
          submitData.organization_associations = formData.organization_associations.map(assoc => ({
            organization_id: assoc.organization_id,
            user_id_access_list: assoc.user_id_access_list || []
          }));
        } else {
          submitData.organization_associations = [];
        }
        
        const response = await onSave(submitData);
        
        // Check if response contains an access key (only on creation)
        if (response && response.access_key) {
          setNewAccessKey(response.access_key);
          setShowAccessKey(true);
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('Error saving application:', error);
      setErrors({ submit: 'Failed to save application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyAccessKey = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(newAccessKey);
        alert('Access key copied to clipboard!');
      } else {
        // Fallback method
        const textArea = document.createElement('textarea');
        textArea.value = newAccessKey;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('Access key copied to clipboard!');
      }
    } catch (err) {
      console.error('Error copying access key:', err);
      prompt('Copy the access key below:', newAccessKey);
    }
  };

  const handleCloseAccessKeyModal = () => {
    setShowAccessKey(false);
    setNewAccessKey('');
    onClose();
  };

  if (!isOpen && !showAccessKey) return null;

  // Show access key modal after successful creation
  if (showAccessKey && newAccessKey) {
    return (
      <>
        <div 
          className="modal-backdrop show" 
          style={{ 
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1040
          }}
        />
        
        <div 
          className="modal show d-block" 
          style={{ zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="ti-check me-2"></i>
                  Application Created Successfully
                </h5>
              </div>
              
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="ti-alert me-2"></i>
                  <strong>Important:</strong> Save this access key now. It won't be shown again!
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Access Key:</label>
                  <div className="input-group">
                    <input 
                      type="text" 
                      className="form-control font-monospace"
                      value={newAccessKey}
                      readOnly
                    />
                    <button 
                      className="btn btn-outline-secondary"
                      onClick={copyAccessKey}
                    >
                      <i className="ti-clipboard"></i> Copy
                    </button>
                  </div>
                </div>
                
                <div className="text-muted small">
                  Store this key securely. You'll need it to authenticate your application.
                </div>
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleCloseAccessKeyModal}
                >
                  I've Saved the Key
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div 
        className="modal-backdrop show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1040
        }}
        onClick={onClose}
      />
      
      <div 
        className="modal show d-block" 
        style={{ zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title || 'Application'}</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {loadingAppData ? (
                  <div className="text-center p-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading application data...</span>
                    </div>
                    <p className="mt-2">Loading application data...</p>
                  </div>
                ) : (
                  <>
                    {errors.submit && (
                      <div className="alert alert-danger" role="alert">
                        {errors.submit}
                      </div>
                    )}
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Application Name <span className="text-danger">*</span>
                          </label>
                          <input 
                            type="text" 
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                          />
                          {errors.name && (
                            <div className="invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                        </div>
                      </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Application Purpose</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="detail.application_purpose"
                        value={formData.detail.application_purpose || ''}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Application Author</label>
                      <input 
                        type="text" 
                        className="form-control"
                        name="detail.application_author"
                        value={formData.detail.application_author || ''}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">External Sync Configuration <span className="text-muted">(Optional)</span></h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Sync Endpoint URL</label>
                        <input 
                          type="url" 
                          className="form-control"
                          name="application_sync_endpoint"
                          value={formData.application_sync_endpoint}
                          onChange={handleChange}
                          placeholder="https://api.example.com/sync"
                        />
                        <div className="form-text">URL endpoint for syncing application data with external systems</div>
                      </div>
                    </div>
                    
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Sync API Key</label>
                        <input 
                          type="password" 
                          className="form-control"
                          name="application_sync_api_key"
                          value={formData.application_sync_api_key}
                          onChange={handleChange}
                          placeholder="sk_live_abc123xyz789"
                        />
                        <div className="form-text">API key for authenticating with the sync endpoint</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <PlatformEndpointsEditor
                    endpoints={formData.platform_communication_endpoints}
                    onChange={handlePlatformEndpointsChange}
                    disabled={isSubmitting || loadingAppData}
                  />
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">Application Type</h6>
                  <div className="form-check form-switch">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="isServiceWorker"
                      name="is_service_worker"
                      checked={formData.is_service_worker}
                      onChange={handleChange}
                    />
                    <label className="form-check-label" htmlFor="isServiceWorker">
                      Service Worker Application
                    </label>
                    <div className="form-text">
                      Enable this if the application is a background service worker that doesn't require user interaction
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="mb-3">Organization Access Management</h6>
                  {formData.is_service_worker ? (
                    <div className="alert alert-info" role="alert">
                      <i className="ti-info-alt me-2"></i>
                      Service Workers cannot be associated with a specific organization. Convert to non-service worker application type to allow for organization specific relationships.
                    </div>
                  ) : (
                    <>
                      {errors.organizations && (
                        <div className="alert alert-danger" role="alert">
                          {errors.organizations}
                        </div>
                      )}
                      
                      <MultiOrgSelector
                        organizations={organizations}
                        selectedOrgs={formData.organization_associations}
                        onChange={handleOrganizationAssociationsChange}
                      />
                    </>
                  )}
                </div>
                  </>
                )}
              </div>
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                  disabled={isSubmitting || loadingAppData}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={isSubmitting || loadingAppData}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Application'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ApplicationModal;